"use client";

import { useMemo } from "react";
import { isAddress, zeroAddress } from "viem";
import { useReadContract } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { MAX_DIRECT_CHILDREN } from "./useParentFieldValidation";

/**
 * Checks capacity for an address ONLY in its role as an *implicit default
 * parent* -- i.e. when the buyer leaves the parent field blank and the
 * contract falls back to placing them under their referrer / locked referrer
 * (Users._resolvePlacementParent). Deliberately separate from
 * {@link useReferrerFieldValidation}, which never checks capacity because a
 * referrer does not need a free slot just to be credited the referral cut --
 * only when that same address is ALSO about to become the tree parent (the
 * default-parent case) does capacity matter, and the contract will revert
 * with `ParentCapacityReached` if it does not have one.
 */
export const useDefaultParentCapacity = (
  candidateAddress: string | undefined,
  enabled: boolean,
) => {
  const isWellFormed = !!candidateAddress && isAddress(candidateAddress);
  const shouldRead = enabled && isWellFormed;

  const query = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: GATEWAY_ABI,
    functionName: "getUser",
    args: [(candidateAddress ?? zeroAddress) as `0x${string}`],
    query: { enabled: shouldRead },
  });

  // Never block on an unresolved/unverified read -- only report "full" once
  // the chain has actually confirmed it. Optional-chained throughout: a
  // malformed or not-yet-populated read (e.g. `{ exists: false }` with no
  // `bv`) must fail open, not throw.
  const isFull = useMemo(() => {
    if (!shouldRead) return false;
    const user = query.data as { bv?: { dir?: bigint } } | undefined;
    return (user?.bv?.dir ?? 0n) >= MAX_DIRECT_CHILDREN;
  }, [shouldRead, query.data]);

  return {
    isLoading: shouldRead && query.isLoading,
    isFull,
  };
};
